import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SectionCard } from "../ui/section-card";
import { ScoreBadge } from "../ui/score-card";
import {
  coerceStoryCraftAssessment,
  emptyStoryCraftAssessment,
  type StoryCraftAssessment,
  type StoryCraftScoreNotes,
} from "../../../shared/storyCraftFeedback";
import { averageAssessmentScore } from "../../../shared/scoreBlend";
import { userFacingError } from "../../../shared/userFacingError";

const SCORE_DIMENSIONS: Array<{
  key: keyof Omit<StoryCraftAssessment, "overallNotes">;
  label: string;
}> = [
  { key: "plotProgression", label: "Plot progression" },
  { key: "characterDevelopment", label: "Character development" },
  { key: "themeReinforcement", label: "Theme reinforcement" },
  { key: "pacing", label: "Pacing" },
  { key: "conflictTension", label: "Conflict & tension" },
  { key: "hookEnding", label: "Hook & ending" },
];

function scoreLabel(avg: number | null): string {
  if (avg == null) return "";
  if (avg >= 5) return "Excellent";
  if (avg >= 4) return "Good";
  if (avg >= 3) return "Fair";
  if (avg >= 2) return "Needs work";
  return "Poor";
}

export type DesktopEditorFeedback = {
  assessment: StoryCraftAssessment;
  summary: string | null;
  lastUpdated?: string | null;
};

export function editorFeedbackFromChapterFields(
  customFields: Record<string, unknown> | null | undefined
): DesktopEditorFeedback | null {
  if (!customFields || typeof customFields !== "object") return null;
  if (!customFields.editorAssessment && customFields.editorAverage == null) return null;
  return {
    assessment: coerceStoryCraftAssessment(customFields.editorAssessment),
    summary: typeof customFields.editorSummary === "string" ? customFields.editorSummary : null,
    lastUpdated:
      typeof customFields.editorFeedbackUpdatedAt === "string"
        ? customFields.editorFeedbackUpdatedAt
        : null,
  };
}

export function EditorScoreForm({
  initial,
  canEdit,
  busy = false,
  onSave,
}: {
  initial: DesktopEditorFeedback | null;
  canEdit: boolean;
  busy?: boolean;
  onSave: (next: {
    assessment: StoryCraftAssessment;
    summary: string | null;
    average: number | null;
  }) => Promise<void> | void;
}) {
  const [assessment, setAssessment] = useState<StoryCraftAssessment>(() =>
    initial ? coerceStoryCraftAssessment(initial.assessment) : emptyStoryCraftAssessment()
  );
  const [summary, setSummary] = useState(initial?.summary ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(initial?.lastUpdated ?? null);

  useEffect(() => {
    setAssessment(
      initial ? coerceStoryCraftAssessment(initial.assessment) : emptyStoryCraftAssessment()
    );
    setSummary(initial?.summary ?? "");
    setSavedAt(initial?.lastUpdated ?? null);
  }, [initial]);

  const grade = averageAssessmentScore(assessment);

  function updateDim(
    key: keyof Omit<StoryCraftAssessment, "overallNotes">,
    patch: Partial<StoryCraftScoreNotes>
  ) {
    setAssessment((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  }

  async function save(): Promise<void> {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const average = averageAssessmentScore(assessment);
      await onSave({
        assessment,
        summary: summary.trim() || null,
        average,
      });
      setSavedAt(new Date().toISOString());
    } catch (err) {
      setError(userFacingError(err, "generic"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-208 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="m-0 text-sm font-semibold text-foreground">Editor score</h4>
        {grade != null ? (
          <ScoreBadge score={grade} label={scoreLabel(grade)} />
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </div>
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        Same rubric as Story Craft. Scores feed chapter-list borders when blend mode includes editor.
      </p>

      <div className="flex flex-col gap-3">
        {SCORE_DIMENSIONS.map(({ key, label }) => (
          <SectionCard
            key={key}
            title={label}
            actions={
              <Select
                value={String(assessment[key].score)}
                onValueChange={(value) => updateDim(key, { score: Number(value) })}
                disabled={!canEdit || saving || busy}
              >
                <SelectTrigger className="min-w-18" size="sm" aria-label={`${label} score`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}/5
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          >
            <Textarea
              rows={2}
              disabled={!canEdit || saving || busy}
              placeholder="Notes…"
              value={assessment[key].notes}
              onChange={(event) => updateDim(key, { notes: event.target.value })}
            />
          </SectionCard>
        ))}

        <SectionCard title="Summary">
          <Textarea
            rows={3}
            disabled={!canEdit || saving || busy}
            placeholder="Overall editor notes for this chapter…"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
        </SectionCard>
      </div>

      {error ? <p className="m-0 text-sm text-destructive">{error}</p> : null}
      {savedAt ? (
        <p className="m-0 text-xs text-muted-foreground">
          Saved {new Date(savedAt).toLocaleString()}
        </p>
      ) : null}

      {canEdit ? (
        <Button type="button" size="sm" disabled={saving || busy} onClick={() => void save()}>
          {saving ? "Saving…" : "Save editor score"}
        </Button>
      ) : null}
    </div>
  );
}
